import { Component } from '@theme/component';
import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

// This factory is embedded into henry-accessories.js by build-patch.mjs.
// It has no storage, cookies, customer identifiers, cart tokens or URL payloads.
function createAccessoryAnalytics({win = window, doc = document,
  Observer = win.IntersectionObserver, schedule = win.setTimeout.bind(win),
  cancel = win.clearTimeout.bind(win)} = {}) {
  const endpoint = 'https://henry-upsell.doimage.workers.dev/henry-upsell/analytics/events';
  const roots = new Set();
  const cards = new Map();
  const visits = new Set();
  const impressions = new Set();
  const requests = new Set();
  let visitId = null;
  let featureRequested = false;
  const uuid = () => win.crypto?.randomUUID?.();
  const allowed = () => {
    try {
      const privacy = win.Shopify?.customerPrivacy;
      return privacy?.currentVisitorConsent?.().analytics === 'yes' &&
        privacy?.analyticsProcessingAllowed?.() === true;
    } catch { return false; }
  };
  const surface = (root) => root.localName === 'henry-cart-sequence' ? 'cart' : 'product';
  const pairing = (card) => {
    const root = card?.closest('henry-accessories, henry-cart-sequence');
    const accessoryProductId = card?.dataset.accessoryId;
    const sourceProductId = card?.dataset.sourceId;
    if (!root || !/^\d+$/.test(accessoryProductId || '') || !/^\d+$/.test(sourceProductId || '')) return null;
    const place = surface(root);
    return {surface: place, accessoryProductId, sourceProductId,
      offerId: `h1-${place}-${sourceProductId}-${accessoryProductId}`};
  };
  const visible = (element, entry) => {
    if (doc.visibilityState !== 'visible' || !element.isConnected || !entry?.isIntersecting ||
        element.closest('[hidden], dialog:not([open])') || entry.isVisible === false) return false;
    for (let node = element; node; node = node.parentElement) {
      const style = win.getComputedStyle(node);
      if (style.display === 'none' || style.visibility === 'hidden' || style.visibility === 'collapse' || Number(style.opacity) === 0) return false;
    }
    const rect = entry.intersectionRect;
    if (!rect || rect.width <= 0 || rect.height <= 0) return false;
    const top = doc.elementFromPoint(rect.left + rect.width / 2, rect.top + rect.height / 2);
    return Boolean(top && element.contains(top));
  };
  function stopTimer(state) {
    if (state.timer != null) cancel(state.timer);
    state.timer = null;
  }
  function clearConsentState() {
    for (const state of cards.values()) stopTimer(state);
    for (const request of requests) request.abort();
    requests.clear();
    visitId = null;
    visits.clear();
    impressions.clear();
  }
  function record(event, fields) {
    if (!allowed()) { clearConsentState(); return false; }
    visitId ||= uuid();
    const eventId = uuid();
    if (!visitId || !eventId) return false;
    const controller = new AbortController();
    requests.add(controller);
    // Never delay the cart operation or send referrer URLs/cookies to analytics.
    try {
      Promise.resolve(win.fetch(endpoint, {
        method: 'POST', mode: 'cors', credentials: 'omit', referrerPolicy: 'no-referrer', keepalive: true,
        headers: {'Content-Type': 'application/json'}, signal: controller.signal,
        body: JSON.stringify({eventId, visitId, event, ...fields, occurredAt: new Date().toISOString(), consent: true}),
      })).catch(() => {}).finally(() => requests.delete(controller));
    } catch { requests.delete(controller); }
    return true;
  }
  function visit(place) {
    if (visits.has(place) || doc.visibilityState !== 'visible') return;
    if (record('visit', {surface: place})) visits.add(place);
  }
  function consider(card, state) {
    stopTimer(state);
    if (!allowed() || state.entry?.intersectionRatio < 0.5 || !visible(card, state.entry)) return;
    const offer = pairing(card);
    if (!offer || impressions.has(offer.offerId)) return;
    state.timer = schedule(() => {
      state.timer = null;
      if (!allowed() || state.entry?.intersectionRatio < 0.5 || !visible(card, state.entry)) return;
      const current = pairing(card);
      if (!current || current.offerId !== offer.offerId || impressions.has(offer.offerId)) return;
      visit(offer.surface);
      if (record('impression', offer)) impressions.add(offer.offerId);
    }, 1000);
  }
  const observer = Observer ? new Observer((entries) => {
    for (const entry of entries) {
      const state = cards.get(entry.target);
      if (state) { state.entry = entry; consider(entry.target, state); }
      else if (roots.has(entry.target) && surface(entry.target) === 'cart' && allowed() && visible(entry.target, entry)) visit('cart');
    }
  }, {threshold: [0, 0.5]}) : null;
  function refresh(root) {
    for (const [card, state] of cards) {
      if (state.root === root && (!card.isConnected || !root.contains(card))) {
        stopTimer(state); observer?.unobserve(card); cards.delete(card);
      }
    }
    for (const card of root.querySelectorAll('[data-accessory-id][data-source-id]')) {
      const offerId = pairing(card)?.offerId ?? null;
      const state = cards.get(card);
      if (!state) {
        cards.set(card, {root, offerId, entry: null, timer: null}); observer?.observe(card);
      } else if (state.offerId !== offerId) {
        // A section morph can reuse an article with new product/source data
        // without changing its geometry. Request a fresh intersection for it.
        stopTimer(state); state.offerId = offerId; state.entry = null;
        observer?.unobserve(card); observer?.observe(card);
      }
    }
    if (surface(root) === 'product' && allowed()) visit('product');
  }
  function syncConsent() {
    if (!allowed()) { clearConsentState(); return; }
    for (const root of roots) {
      refresh(root);
      // Re-observe to obtain a fresh intersection after consent/visibility changes.
      observer?.unobserve(root); observer?.observe(root);
    }
    for (const [card, state] of cards) {
      stopTimer(state); observer?.unobserve(card); observer?.observe(card);
    }
  }
  function loadPrivacy() {
    syncConsent();
    if (win.Shopify?.customerPrivacy || featureRequested || !win.Shopify?.loadFeatures) return;
    featureRequested = true;
    try { win.Shopify.loadFeatures([{name: 'consent-tracking-api', version: '0.1'}], (error) => { if (!error) syncConsent(); }); } catch {}
  }
  const onVisibility = () => {
    if (doc.visibilityState !== 'visible') { for (const state of cards.values()) stopTimer(state); }
    else loadPrivacy();
  };
  doc.addEventListener('visitorConsentCollected', syncConsent);
  doc.addEventListener('visibilitychange', onVisibility);
  win.addEventListener('load', loadPrivacy, {once: true});
  loadPrivacy();
  return {
    connect(root) { roots.add(root); observer?.observe(root); refresh(root); loadPrivacy(); },
    refresh,
    disconnect(root) {
      roots.delete(root); observer?.unobserve(root);
      for (const [card, state] of cards) if (state.root === root) {
        stopTimer(state); observer?.unobserve(card); cards.delete(card);
      }
    },
    prepareAdd(form, variantId, quantity) {
      if (!allowed() || !/^\d+$/.test(variantId) || !Number.isInteger(quantity) || quantity < 1 || quantity > 99) return null;
      const offer = pairing(form.closest('[data-accessory-id]'));
      if (!offer) return null;
      return {...offer, variantId, quantity};
    },
    confirmedAdd(offer) { if (offer) { visit(offer.surface); record('add', offer); } },
    rejectedAdd(offer) { if (offer) { visit(offer.surface); record('add_error', offer); } },
    destroy() {
      clearConsentState(); observer?.disconnect(); roots.clear(); cards.clear();
      doc.removeEventListener('visitorConsentCollected', syncConsent);
      doc.removeEventListener('visibilitychange', onVisibility);
      win.removeEventListener('load', loadPrivacy);
    },
  };
}

const accessoryAnalytics = createAccessoryAnalytics();

// Kept outside morphed cart markup so an add confirmation survives section rendering.
let lastAdded = null;
let pendingAdd = false;
let eventVersion = 0;

const refreshSequences = () => {
  for (const sequence of document.querySelectorAll('henry-cart-sequence')) sequence.refresh();
};

document.addEventListener(StandardEvents.cartLinesUpdate, async (event) => {
  const version = ++eventVersion;
  try {
    const { detail } = (await event.promise) || {};
    if (version !== eventVersion || detail?.didError) return;
    if (Array.isArray(detail?.items)) {
      const productIds = new Set(detail.items.map((item) => String(item.product_id)));
      for (const card of document.querySelectorAll('henry-accessories [data-accessory-id]')) {
        card.hidden = productIds.has(card.dataset.accessoryId);
      }
    }
    if (event.action === 'add') {
      const line = event.lines?.[0];
      const variantId = String(line?.merchandiseId || '').split('/').pop();
      const item = detail?.items?.find((item) => String(item.variant_id || item.id) === variantId);
      if (item) lastAdded = { item, quantity: Number(line.quantity) || 1 };
    } else {
      lastAdded = null;
    }
    refreshSequences();
    if (detail?.source === 'henry-accessories') {
      requestAnimationFrame(() => {
        const heading = document.getElementById('pd-added-title');
        if (heading?.closest('dialog')?.open && lastAdded) {
          heading.setAttribute('tabindex', '-1');
          heading.focus();
        }
      });
    }
  } catch (error) {
    if (error.name !== 'AbortError') console.warn('[accessories] Cart update failed', error);
  }
});

class HenryAccessories extends Component {
  #controller;

  connectedCallback() {
    super.connectedCallback();
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    this.addEventListener('submit', this.#add, { signal });
    this.addEventListener('change', this.#changeVariant, { signal });
    accessoryAnalytics.connect(this);
  }

  updatedCallback() {
    super.updatedCallback();
    accessoryAnalytics.refresh(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller?.abort();
    accessoryAnalytics.disconnect(this);
  }

  #changeVariant = (event) => {
    if (!(event.target instanceof HTMLSelectElement)) return;
    const option = event.target.selectedOptions[0];
    const card = event.target.closest('[data-accessory-id]');
    const price = card?.querySelector('[data-accessory-price]');
    const quantity = card?.querySelector('input[name="quantity"]');
    if (price && option?.dataset.price) price.textContent = option.dataset.price;
    if (quantity) quantity.value = option?.dataset.minimum || '1';
  };

  #add = async (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.matches('[data-accessory-form]')) return;
    event.preventDefault();
    if (pendingAdd || !form.reportValidity()) return;
    pendingAdd = true;
    const status = this.querySelector('[data-accessory-status]');
    const submit = form.querySelector('button[type="submit"]');
    const originalText = submit?.textContent;
    if (status) status.textContent = '';
    if (submit) {
      submit.disabled = true;
      submit.textContent = this.dataset.adding;
    }
    form.setAttribute('aria-busy', 'true');
    const data = new FormData(form);
    const variantId = String(data.get('id'));
    const quantity = Number(data.get('quantity')) || 1;
    const analyticsOffer = accessoryAnalytics.prepareAdd(form, variantId, quantity);
    if (analyticsOffer) {
      // Sent with the cart operation; persisted only if Shopify confirms the add.
      data.set('properties[_henry_surface]', analyticsOffer.surface);
      data.set('properties[_henry_source_product]', analyticsOffer.sourceProductId);
      data.set('properties[_henry_offer_id]', analyticsOffer.offerId);
    }
    const sectionIds = [...new Set([...document.querySelectorAll('cart-items-component')]
      .map((component) => component.dataset.sectionId).filter(Boolean))].slice(0, 5);
    data.set('sections', sectionIds.join(','));
    data.set('sections_url', window.location.pathname);
    let committed = false;
    try {
      // A cart mutation must finish even if section rendering disconnects this component.
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST', body: data, headers: { Accept: 'application/json' },
      });
      const added = await response.json();
      if (!response.ok || added.status) {
        accessoryAnalytics.rejectedAdd(analyticsOffer);
        throw new Error(added.description || added.message || this.dataset.error);
      }
      committed = true;
      accessoryAnalytics.confirmedAdd(analyticsOffer);
      const cartResponse = await fetch(Theme.routes.cart_url + '.js', { headers: { Accept: 'application/json' } });
      if (!cartResponse.ok) throw new Error(this.dataset.error);
      const cart = await cartResponse.json();
      const result = {
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { items: cart.items, sections: added.sections, didError: false, source: 'henry-accessories' },
      };
      (this.isConnected ? this : document).dispatchEvent(new CartLinesUpdateEvent({
        action: 'add', context: 'product',
        lines: [{ merchandiseId: variantId, quantity }], promise: Promise.resolve(result),
      }));
    } catch (error) {
      if (committed) {
        // The add succeeded but refreshing failed. Reload the basket; never retry an add.
        window.location.assign(Theme.routes.cart_url);
      } else if (status) {
        status.textContent = error.message || this.dataset.error;
      }
    } finally {
      pendingAdd = false;
      form.removeAttribute('aria-busy');
      if (submit?.isConnected) {
        submit.disabled = false;
        submit.textContent = originalText;
      }
    }
  };
}

class HenryCartSequence extends HenryAccessories {
  #controller;

  connectedCallback() {
    super.connectedCallback();
    this.#controller = new AbortController();
    this.closest('cart-drawer-component')?.addEventListener('click', this.#viewBag, { signal: this.#controller.signal });
    this.refresh();
  }

  updatedCallback() {
    super.updatedCallback();
    this.refresh();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller?.abort();
  }

  #viewBag = (event) => {
    if (!(event.target instanceof Element) || !event.target.closest('[data-view-bag]')) return;
    lastAdded = null;
    refreshSequences();
    const content = this.closest('.cart-drawer__content');
    if (content) content.scrollTop = 0;
    const heading = document.getElementById('cart-drawer-heading');
    if (heading) {
      heading.setAttribute('tabindex', '-1');
      heading.focus();
    }
  };

  refresh() {
    const panel = this.querySelector('[data-added-panel]');
    if (!panel) return;
    panel.hidden = !lastAdded;
    this.toggleAttribute('data-confirming', Boolean(lastAdded));
    this.closest('dialog')?.setAttribute('aria-labelledby', lastAdded ? 'pd-added-title' : 'cart-drawer-heading');
    if (lastAdded) {
      const { item, quantity } = lastAdded;
      const title = this.querySelector('[data-added-title]');
      title.textContent = item.product_title || item.title;
      title.href = item.url;
      this.querySelector('[data-added-variant]').textContent = item.variant_title === 'Default Title' ? '' : item.variant_title || '';
      this.querySelector('[data-added-quantity]').textContent = String(quantity);
      const money = new Intl.NumberFormat(document.documentElement.lang || 'en-GB', {
        style: 'currency', currency: window.Shopify?.currency?.active || 'GBP',
      });
      this.querySelector('[data-added-price]').textContent = money.format((item.final_price ?? item.price) * quantity / 100);
      const original = this.querySelector('[data-added-original]');
      if (original) {
        original.hidden = !(item.original_price > (item.final_price ?? item.price));
        original.textContent = original.hidden ? '' : money.format(item.original_price * quantity / 100);
      }
      const image = this.querySelector('[data-added-image]');
      const src = item.featured_image?.url || item.image;
      image.hidden = !src;
      image.alt = item.featured_image?.alt || item.product_title || item.title;
      if (src) image.src = src;
    }
    const cards = [...this.querySelectorAll('[data-accessory-id]')];
    const priority = String(lastAdded?.item.product_id || '');
    cards.sort((a, b) => Number(b.dataset.sourceId === priority) - Number(a.dataset.sourceId === priority));
    const selected = new Set();
    for (const card of cards) {
      const visible = !selected.has(card.dataset.accessoryId);
      card.hidden = !visible;
      if (visible) selected.add(card.dataset.accessoryId);
      card.style.order = visible ? String(selected.size) : '';
    }
  }
}

if (!customElements.get('henry-accessories')) customElements.define('henry-accessories', HenryAccessories);
if (!customElements.get('henry-cart-sequence')) customElements.define('henry-cart-sequence', HenryCartSequence);

// Closing the drawer ends the confirmation step; a later bag visit shows its contents.
document.addEventListener('theme-drawer:close', (event) => {
  if (event.target?.id !== 'cart-drawer') return;
  lastAdded = null;
  refreshSequences();
});
